import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { Swipeable } from 'react-swipeable';
import NextIcon from '@material-ui/icons/SkipNext';
import PrevIcon from '@material-ui/icons/SkipPrevious';
import Fab from '@material-ui/core/Fab';
import Play from '@material-ui/icons/VolumeMute';
import Playing from '@material-ui/icons/VolumeUp';
import Waiting from '@material-ui/icons/VolumeDown';
import Ok from '@material-ui/icons/Done';
import Wrong from '@material-ui/icons/Clear';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  wrap: {
    maxWidth: '100%',
    width: 600,
    margin: '0 auto',
    fontSize: '1.75em',
    color: '#333',
    '& > h3': {
      textAlign: 'center',
      margin: '0 0 0.5em',
      padding: '0.25em 0',
    },
    '& > div': {
      display: 'flex',
    },
  },
  fab: {
    margin: theme.spacing(1),
    marginRight: '0.5em',
  },
  word: {
    cursor: 'pointer',
    userSelect: 'none',
    width: '100%',
    padding: '0 0.5em',
    borderRadius: '1em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& svg': {
      width: '1.5em',
      height: '1.5em',
    },
    '&:hover': {
      background: '#eee',
    },
  },
  icons: {
    minWidth: '3.5em',
    display: 'flex',
  },
  extendedIcon: {
    marginRight: theme.spacing(1),
  },
  nav: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '0.5em',
    '& button': {
      margin: '0.5em',
    },
    '& svg': {
      width: '1.25em',
      height: '1.25em',
    },
    '& > div': {
      fontSize: '1.5rem',
      margin: '0 0.25em',
      minWidth: '3.5em',
      textAlign: 'center',
    }
  },
  correct: {
    color: 'green',
    fontWeight: 600,
  },
  wrong: {
    '& svg': {
      color: '#de0000',
    },
  },
}));

function Words(props) {
  const classes = useStyles();
  const [currentSound, setCurrentSound] = useState({ path: '', playing: false });
  const [no, setNo] = useState(0);
  const [correctNo, setCorrectNo] = useState(0);
  const [resultArr, setResultArr] = useState([null, null, null, null]);
  const [activeNext, setActiveNext] = useState(false);
  const { exercise, expand, onExpand, startStats, finishExercise, action, type } = props.exerciseProps;
  const showNav = !(expand && props.mobile);

  const play = (ev, path) => {
    action();
    ev.stopPropagation();
    props.media.play(path);
  }

  useEffect(() => {
    startStats(type);
    const { playing, pause, waiting } = props.media.events;
    const playSubscribtion = playing.subscribe((ev) => {
      setCurrentSound({ path: ev?.target?.src, playing: true });
    });
    const pauseSubscribtion = pause.subscribe((ev) => {
      setCurrentSound({ path: '', playing: false });
    });
    const waitingSubscribtion = waiting.subscribe((ev) => {
      setCurrentSound({ path: ev?.target?.src, playing: false });
    });
    return () => {
      playSubscribtion.unsubscribe();
      pauseSubscribtion.unsubscribe();
      waitingSubscribtion.unsubscribe();
    }
    // eslint-disable-next-line
  }, []);

  const wordClick = (ev, wordNo, soundPath) => {
    action();
    if (resultArr[wordNo - 1] !== null) return;
    const resultArrCopy = [...resultArr];
    const result = correctNo === wordNo;
    resultArrCopy[wordNo - 1] = result;
    setResultArr(resultArrCopy);
    play(ev, soundPath);
    if (result) {
      setActiveNext(true);
      if (no === exercise.length - 1) {
        props.media.finish();
        finishExercise();
        if (expand) onExpand();
      } else {
        // props.media.success();
      }
    } else {
      // props.media.failure();
      // console.log();
    }
  }

  const renderTitle = () => {
    const correct = exercise[no].correct;
    const title = exercise[no]['word' + correct].translation;
    if (correctNo !== correct) setCorrectNo(correct);
    return <h3 style={{ background: props.mobile ? 'transparent' : '#eee' }}>{title}</h3>
  }

  const renderWord = (wordNo) => {
    const word = exercise[no]['word' + wordNo];
    const soundPath = props.media.audioPath(word.soundUrl);
    let className = '';
    let icon = '';

    if (resultArr[wordNo - 1] === true) {
      className = classes.correct;
      icon = <Ok />;
    }

    if (resultArr[wordNo - 1] === false) {
      className = classes.wrong;
      icon = <Wrong />;
    }

    return (
      <div>
        <Fab
          variant="extended"
          size="medium"
          color="primary"
          className={classes.fab}
          onClick={(ev) => play(ev, soundPath)}
        >
          {(currentSound.path === soundPath && currentSound.playing) && <Playing />}
          {(currentSound.path === soundPath && !currentSound.playing) && <Waiting />}
          {currentSound.path !== soundPath && <Play />}
        </Fab>
        <span className={clsx(classes.word, className)} onClick={ev => wordClick(ev, wordNo, soundPath)}>
          {word.text}
          {icon}
        </span>
      </div>
    );
  }

  const renderWords = () => {
    return (
      <>
        {renderWord(1)}
        {renderWord(2)}
        {renderWord(3)}
        {renderWord(4)}
      </>
    );
  }

  const prev = () => {
    action();
    if (no > 0) {
      setNo(no - 1);
      setResultArr([null, null, null, null]);
    }
  }

  const next = () => {
    action();
    if (no < exercise.length - 1 && activeNext) {
      setNo(no + 1);
      setResultArr([null, null, null, null]);
      setActiveNext(false);
    }
  }

  return (
    <Swipeable className={classes.container} onSwipedRight={prev} onSwipedLeft={next} delta={40} >
      <div />
      <div className={classes.wrap}>

        {renderTitle()}
        {renderWords()}

        {showNav && <div className={classes.nav}>
          <Fab color="secondary" onClick={prev} disabled={no === 0}>
            <PrevIcon />
          </Fab>
          <div>{no + 1} / {exercise.length}</div>
          <Fab color="secondary" onClick={next} disabled={exercise.length - 1 === no || !activeNext}>
            <NextIcon />
          </Fab>
        </div>}

      </div>
      <div />
      <div />
    </Swipeable>
  );
}

const mapStateToProps = (state) => {
  return {
    media: state.media,
    mobile: state.mobile,
  }
}

export default connect(mapStateToProps)(Words);